/**
 * NotificationDrawer —— 通知中心抽屉：收件箱列表 + 单条已读 + 全部已读 + 深链跳转。
 *
 * 数据源：GET /notifications/inbox（分页，最新在前）。条目点击行为：
 *  1. 未读 → 先 PUT 已读（乐观不等结果，mutation 成功后 invalidate 刷新角标）；
 *  2. ref_type 可解析 → 路由跳转（映射表见 constants/notificationDeepLinks.ts，
 *     唯一登记处）；未知 ref_type 降级为纯文本，点击不跳转（仅已读）。
 * SSE 断线降级时由父组件（NotificationBell）注入 refetchInterval 做 60s 轮询对账。
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Drawer, Pagination, Spin } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationInbox,
} from "../../hooks/useNotifications";
import { resolveNotificationRoute } from "../../constants/notificationDeepLinks";
import { formatRelativeTime } from "../../utils";
import type { NotificationInboxItem } from "../../types/notification";
import styles from "../../styles/notifications.module.css";

/** 界面文案常量（禁止 JSX 内联中文标签字符串）。 */
const NOTIFICATION_DRAWER_TEXT = {
  title: "通知中心",
  markAll: "全部已读",
  markAllAria: "将全部通知标为已读",
  empty: "暂无通知",
  loading: "通知加载中...",
  loadError: "通知加载失败",
  retry: "重试",
  viewDetail: "查看详情",
} as const;

const PAGE_SIZE = 20;

interface NotificationDrawerProps {
  open: boolean;
  onClose: () => void;
  /** SSE 降级轮询间隔（false = 不轮询）。 */
  refetchInterval?: number | false;
}

export default function NotificationDrawer({
  open,
  onClose,
  refetchInterval = false,
}: NotificationDrawerProps) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, refetch } = useNotificationInbox(
    { page, pageSize: PAGE_SIZE },
    { refetchInterval },
  );
  const markReadMut = useMarkNotificationRead();
  const markAllMut = useMarkAllNotificationsRead();

  const handleItemClick = (item: NotificationInboxItem) => {
    // 未读条目点击即置已读（fire-and-forget，成功后由 invalidate 刷新列表与角标）
    if (!item.isRead) markReadMut.mutate(item.id);
    const route = resolveNotificationRoute(item.refType, item.refId);
    if (route) {
      onClose();
      navigate(route);
    }
  };

  return (
    <Drawer
      title={NOTIFICATION_DRAWER_TEXT.title}
      open={open}
      onClose={onClose}
      width={420}
      destroyOnHidden
      extra={
        <Button
          size="small"
          icon={<CheckOutlined />}
          onClick={() => markAllMut.mutate()}
          loading={markAllMut.isPending}
          data-testid="notification-mark-all-button"
          aria-label={NOTIFICATION_DRAWER_TEXT.markAllAria}
        >
          {NOTIFICATION_DRAWER_TEXT.markAll}
        </Button>
      }
    >
      <div className={styles.drawerBody} data-testid="notification-drawer-body">
        {isLoading ? (
          <div className={styles.empty} data-testid="notification-drawer-loading">
            <Spin size="small" /> {NOTIFICATION_DRAWER_TEXT.loading}
          </div>
        ) : isError ? (
          <div className={styles.empty} data-testid="notification-drawer-error">
            {NOTIFICATION_DRAWER_TEXT.loadError}
            <Button size="small" type="link" onClick={() => refetch()}>
              {NOTIFICATION_DRAWER_TEXT.retry}
            </Button>
          </div>
        ) : !data || data.data.length === 0 ? (
          <div className={styles.empty} data-testid="notification-drawer-empty">
            {NOTIFICATION_DRAWER_TEXT.empty}
          </div>
        ) : (
          <>
            {data.data.map((item) => (
              <NotificationDrawerItem
                key={item.id}
                item={item}
                onClick={() => handleItemClick(item)}
              />
            ))}
            {data.meta.totalPages > 1 && (
              <Pagination
                size="small"
                current={page}
                pageSize={PAGE_SIZE}
                total={data.meta.total}
                onChange={setPage}
                showSizeChanger={false}
                simple
                data-testid="notification-drawer-pagination"
              />
            )}
          </>
        )}
      </div>
    </Drawer>
  );
}

function NotificationDrawerItem({
  item,
  onClick,
}: {
  item: NotificationInboxItem;
  onClick: () => void;
}) {
  const route = resolveNotificationRoute(item.refType, item.refId);
  return (
    <button
      type="button"
      className={[
        styles.item,
        item.isRead ? styles.itemRead : styles.itemUnread,
      ].join(" ")}
      onClick={onClick}
      data-testid={`notification-item-${item.id}`}
    >
      <div className={styles.itemMain}>
        <div className={styles.itemTitle}>{item.title}</div>
        {item.body && <div className={styles.itemBody}>{item.body}</div>}
        <div className={styles.itemMeta}>
          <span className={styles.itemTime}>{formatRelativeTime(item.createdAt)}</span>
          {route && <span className={styles.itemLinkHint}>{NOTIFICATION_DRAWER_TEXT.viewDetail}</span>}
        </div>
      </div>
    </button>
  );
}
